import { useState } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, Dumbbell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface TrainingPlan {
  type: "training_plan";
  event_date: string;
  text: string;
  plan_name: string;
  start_date: string;
  is_main_record: boolean;
  structure_type: "group" | "daily";
  day_of_week?: string;
  focus?: string;
  training_groups?: TrainingGroup[];
  notes?: string | null;
}

interface TrainingGroup {
  group_name: string;
  exercises: Exercise[];
}

interface Exercise {
  exercise: string;
  sets: number | null;
  reps: string | null;
  load: number | null;
  load_unit: string | null;
  rest: string | null;
  notes: string | null;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileTrainingPlanDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableTrainingPlan extends TrainingPlan {
  _index?: number;
}

export function SmartfileTrainingPlanDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileTrainingPlanDetailsProps) {
  // Buscar o main_record
  const mainRecord = (session.structured_records || []).find((record: any) =>
    typeof record === 'object' &&
    record !== null &&
    'type' in record &&
    record.type === 'training_plan' &&
    ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
  ) as TrainingPlan | undefined;

  // Filtrar apenas training_plans (excluir o registro principal is_main_record: true)
  const trainingPlans: EditableTrainingPlan[] = (session.structured_records || [])
    .filter((record: any): record is TrainingPlan =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'training_plan' &&
      !(record as any).is_main_record // Excluir registro principal
    )
    .map((record: TrainingPlan, index: number) => ({
      ...record,
      _index: index
    }));

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null);
  const [editingExerciseIndex, setEditingExerciseIndex] = useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string | null>(null); // Para rastrear qual grupo está sendo editado
  const [editingMainRecord, setEditingMainRecord] = useState(false); // Para editar main_record (data e nome)
  const [editedRecords, setEditedRecords] = useState<EditableTrainingPlan[]>(trainingPlans);
  const [editedMainRecord, setEditedMainRecord] = useState<TrainingPlan | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);

  // Atualizar editedRecords quando trainingPlans mudar
  React.useEffect(() => {
    setEditedRecords(trainingPlans);
    setEditedMainRecord(mainRecord);
  }, [session.structured_records, mainRecord]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let trainingPlanCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'training_plan' && !(record as any).is_main_record) {
          if (trainingPlanCount === index) {
            realIndex = i;
            break;
          }
          trainingPlanCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Plano de treino atualizado com sucesso!");
        setEditingIndex(null);
        setEditingGroupIndex(null);
        setEditingExerciseIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let trainingPlanCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'training_plan' && !(record as any).is_main_record) {
          if (trainingPlanCount === index) {
            realIndex = i;
            break;
          }
          trainingPlanCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Plano de treino excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir plano de treino");
    }
  };

  const handleDeleteExercise = async (planIndex: number, groupIndex: number, exerciseIndex: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real do plano
      let trainingPlanCount = 0;
      let realPlanIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'training_plan' && !(record as any).is_main_record) {
          if (trainingPlanCount === planIndex) {
            realPlanIndex = i;
            break;
          }
          trainingPlanCount++;
        }
      }

      if (realPlanIndex !== -1) {
        const plan = updatedRecords[realPlanIndex] as any;
        if (plan.training_groups && Array.isArray(plan.training_groups) && plan.training_groups[groupIndex]) {
          const updatedGroups = [...plan.training_groups];
          const updatedGroup = { ...updatedGroups[groupIndex] };
          const updatedExercises = [...updatedGroup.exercises];
          updatedExercises.splice(exerciseIndex, 1);

          updatedGroup.exercises = updatedExercises;
          updatedGroups[groupIndex] = updatedGroup;

          plan.training_groups = updatedGroups;
          updatedRecords[realPlanIndex] = plan;

          const { error } = await supabase
            .from('smartfile_sessions')
            .update({ structured_records: updatedRecords as any })
            .eq('id', session.id);

          if (error) throw error;

          toast.success("✅ Exercício excluído com sucesso!");
          onRefresh();
        }
      }
    } catch (error: any) {
      console.error('Erro ao excluir exercício:', error);
      toast.error("❌ Erro ao excluir exercício");
    }
  };

  const handleCancel = () => {
    setEditedRecords(trainingPlans);
    setEditingIndex(null);
    setEditingGroupIndex(null);
    setEditingExerciseIndex(null);
    setEditingGroupName(null);
    setEditingMainRecord(false);
    setEditedMainRecord(mainRecord);
  };

  const handleSaveMainRecord = async () => {
    if (!editedMainRecord) return;

    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'training_plan' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex >= 0) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error) {
      console.error('Erro ao salvar main_record:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (trainingPlans.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum plano de treino encontrado nesta sessão</p>
      </div>
    );
  }

  // Função para ordenar dias da semana
  const getDayOfWeekOrder = (dayOfWeek?: string): number => {
    if (!dayOfWeek) return 999;
    const dayMap: Record<string, number> = {
      'SEGUNDA': 1,
      'SEG': 1,
      'TERÇA': 2,
      'TERCA': 2,
      'TER': 2,
      'QUARTA': 3,
      'QUA': 3,
      'QUINTA': 4,
      'QUI': 4,
      'SEXTA': 5,
      'SEX': 5,
      'SÁBADO': 6,
      'SABADO': 6,
      'SAB': 6,
      'DOMINGO': 7,
      'DOM': 7
    };
    return dayMap[dayOfWeek.toUpperCase()] || 999;
  };

  // Agrupar por data ou estrutura
  const groupedByDate = trainingPlans.reduce((acc, plan, index) => {
    const date = plan.event_date || plan.start_date || session.created_at;
    const dateKey = date.split('T')[0];
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    // Garantir que o _index está definido
    const planWithIndex = { ...plan, _index: plan._index !== undefined ? plan._index : index };
    acc[dateKey].push(planWithIndex);
    return acc;
  }, {} as Record<string, EditableTrainingPlan[]>);

  // Ordenar planos dentro de cada data por day_of_week quando structure_type for "daily"
  Object.keys(groupedByDate).forEach(dateKey => {
    groupedByDate[dateKey].sort((a, b) => {
      // Se ambos têm structure_type "daily" e day_of_week, ordenar por dia da semana
      if (a.structure_type === 'daily' && b.structure_type === 'daily' &&
        a.day_of_week && b.day_of_week) {
        return getDayOfWeekOrder(a.day_of_week) - getDayOfWeekOrder(b.day_of_week);
      }
      // Se apenas um tem structure_type "daily" e day_of_week, priorizar ele
      if (a.structure_type === 'daily' && a.day_of_week) return -1;
      if (b.structure_type === 'daily' && b.day_of_week) return 1;
      // Caso contrário, manter ordem original
      return 0;
    });
  });

  const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <div className="bg-background border-t">
      <div className="p-4 space-y-4">
        {sortedDates.map((dateKey) => {
          // Separar planos por tipo
          const groupPlans = groupedByDate[dateKey].filter(p => p.structure_type === 'group');
          const dailyPlans = groupedByDate[dateKey].filter(p => p.structure_type === 'daily');

          return (
            <div key={dateKey} className="space-y-3">
              {/* Planos do tipo "group" - todos os grupos no mesmo card */}
              {groupPlans.length > 0 && (() => {
                // Agrupar planos por plan_name (GRUPO A, GRUPO B, etc.)
                const plansByGroupName = groupPlans.reduce((acc, plan) => {
                  const groupName = plan.plan_name || '';
                  if (!acc[groupName]) {
                    acc[groupName] = [];
                  }
                  acc[groupName].push(plan);
                  return acc;
                }, {} as Record<string, EditableTrainingPlan[]>);

                // Ordenar grupos por nome (A, B, C, D)
                const sortedGroupNames = Object.keys(plansByGroupName).sort((a, b) => {
                  const aMatch = a.match(/GRUPO\s*([A-Z])/i);
                  const bMatch = b.match(/GRUPO\s*([A-Z])/i);
                  if (aMatch && bMatch) {
                    return aMatch[1].localeCompare(bMatch[1]);
                  }
                  return a.localeCompare(b);
                });

                return (
                  <div key={`${dateKey}-group-card`} className="border rounded-lg p-4 space-y-6">
                    {/* Header do card com data e nome do main_record editáveis */}
                    <div className="flex items-center justify-between border-b pb-3">
                      <div className="flex items-center gap-3 flex-1">
                        {editingMainRecord ? (
                          <div className="flex items-center gap-3 flex-1">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Nome:</Label>
                              <Input
                                value={editedMainRecord?.plan_name || ''}
                                onChange={(e) => {
                                  setEditedMainRecord({
                                    ...editedMainRecord!,
                                    plan_name: e.target.value
                                  });
                                }}
                                className="h-8 text-xs"
                                placeholder="Nome do plano"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Data:</Label>
                              <Input
                                type="date"
                                value={editedMainRecord?.event_date || editedMainRecord?.start_date || dateKey}
                                onChange={(e) => {
                                  setEditedMainRecord({
                                    ...editedMainRecord!,
                                    event_date: e.target.value,
                                    start_date: e.target.value
                                  });
                                  // Atualizar também a data em todos os planos do grupo
                                  const updated = [...editedRecords];
                                  groupPlans.forEach((plan) => {
                                    const planIndex = plan._index ?? 0;
                                    if (updated[planIndex]) {
                                      updated[planIndex] = {
                                        ...updated[planIndex],
                                        event_date: e.target.value,
                                        start_date: e.target.value
                                      };
                                    }
                                  });
                                  setEditedRecords(updated);
                                }}
                                className="h-8 text-xs"
                              />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h3 className="text-lg font-bold">
                              {mainRecord?.plan_name || 'Plano de Treino'} - {format(new Date(dateKey), "dd/MM/yyyy")}
                            </h3>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {editingMainRecord ? (
                          <>
                            <Button
                              size="sm"
                              onClick={handleSaveMainRecord}
                              disabled={isSaving}
                              className="h-8"
                            >
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingMainRecord(false);
                                setEditedMainRecord(mainRecord);
                              }}
                              disabled={isSaving}
                              className="h-8"
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingMainRecord(true);
                            }}
                            className="h-8"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Todos os grupos dentro do mesmo card */}
                    {sortedGroupNames.map((groupName) => {
                      const plans = plansByGroupName[groupName];

                      // Coletar todos os training_groups de todos os planos deste grupo
                      const allTrainingGroups: Array<{
                        trainingGroup: TrainingGroup;
                        planIndex: number;
                        trainingGroupIdx: number;
                        originalIndex: number;
                      }> = [];

                      plans.forEach((plan, planIdx) => {
                        const planOriginalIndex = plan._index ?? planIdx;
                        plan.training_groups?.forEach((trainingGroup, trainingGroupIdx) => {
                          allTrainingGroups.push({
                            trainingGroup,
                            planIndex: planIdx,
                            trainingGroupIdx,
                            originalIndex: planOriginalIndex
                          });
                        });
                      });

                      // Verificar se este grupo está sendo editado
                      const firstPlanOfGroup = plans[0];
                      const firstPlanIndex = firstPlanOfGroup._index ?? 0;
                      const isEditingThisGroup = editingGroupName === groupName;

                      return (
                        <div key={groupName} className="space-y-4">
                          {/* Header do grupo com botão de editar */}
                          <div className="flex items-center justify-between border-b pb-2">
                            <h3 className="text-base font-semibold">{groupName}</h3>
                            <div className="flex gap-2">
                              {isEditingThisGroup ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      // Salvar todos os planos deste grupo
                                      plans.forEach((plan) => {
                                        const planIndex = plan._index ?? 0;
                                        handleSave(planIndex);
                                      });
                                      setEditingGroupName(null);
                                    }}
                                    disabled={isSaving}
                                    className="h-7 text-xs"
                                  >
                                    {isSaving ? "Salvando..." : "Salvar"}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingGroupName(null);
                                      // Restaurar valores originais
                                      setEditedRecords(trainingPlans);
                                    }}
                                    disabled={isSaving}
                                    className="h-7 text-xs"
                                  >
                                    Cancelar
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingGroupName(groupName);
                                  }}
                                  className="h-7 text-xs"
                                >
                                  <Pencil className="h-3 w-3 mr-1" />
                                  Editar
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Focos (PEITO, TRÍCEPS, etc.) como subseções */}
                          {allTrainingGroups.map(({ trainingGroup, originalIndex: tgOriginalIndex, trainingGroupIdx }, focusIdx) => {
                            const focus = trainingGroup.group_name;
                            const exercises = trainingGroup.exercises || [];

                            return (
                              <div key={`${groupName}-${focusIdx}`} className="space-y-2">
                                <h4 className="text-sm font-semibold text-muted-foreground">{focus}</h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse text-sm table-fixed">
                                    <colgroup>
                                      <col className="w-[60%]" />
                                      <col className="w-[20%]" />
                                      <col className="w-[20%]" />
                                    </colgroup>
                                    <thead>
                                      <tr className="bg-muted/50">
                                        <th className="p-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-r">
                                          Exercício
                                        </th>
                                        <th className="p-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-r">
                                          Séries
                                        </th>
                                        <th className="p-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                                          Repetições
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {exercises.map((exercise, exerciseIdx) => (
                                        <tr key={exerciseIdx} className="border-b border-border hover:bg-muted/30 transition-colors">
                                          <td className="p-2 border-r">
                                            {isEditingThisGroup ? (
                                              <Input
                                                value={editedRecords[tgOriginalIndex]?.training_groups?.[trainingGroupIdx]?.exercises?.[exerciseIdx]?.exercise || ''}
                                                onChange={(e) => {
                                                  const updated = [...editedRecords];
                                                  if (!updated[tgOriginalIndex].training_groups) {
                                                    updated[tgOriginalIndex].training_groups = [];
                                                  }
                                                  if (!updated[tgOriginalIndex].training_groups[trainingGroupIdx]) {
                                                    updated[tgOriginalIndex].training_groups[trainingGroupIdx] = {
                                                      group_name: focus,
                                                      exercises: []
                                                    };
                                                  }
                                                  const updatedExercises = [...(updated[tgOriginalIndex].training_groups[trainingGroupIdx].exercises || [])];
                                                  updatedExercises[exerciseIdx] = {
                                                    ...updatedExercises[exerciseIdx],
                                                    exercise: e.target.value
                                                  };
                                                  updated[tgOriginalIndex].training_groups[trainingGroupIdx].exercises = updatedExercises;
                                                  setEditedRecords(updated);
                                                }}
                                                className="h-8 text-xs w-full"
                                                placeholder="Nome do exercício"
                                              />
                                            ) : (
                                              <span className="text-sm block truncate">{exercise.exercise}</span>
                                            )}
                                          </td>
                                          <td className="p-2 border-r text-center">
                                            {isEditingThisGroup ? (
                                              <Input
                                                type="number"
                                                value={editedRecords[tgOriginalIndex]?.training_groups?.[trainingGroupIdx]?.exercises?.[exerciseIdx]?.sets ?? ''}
                                                onChange={(e) => {
                                                  const updated = [...editedRecords];
                                                  if (!updated[tgOriginalIndex].training_groups) {
                                                    updated[tgOriginalIndex].training_groups = [];
                                                  }
                                                  if (!updated[tgOriginalIndex].training_groups[trainingGroupIdx]) {
                                                    updated[tgOriginalIndex].training_groups[trainingGroupIdx] = {
                                                      group_name: focus,
                                                      exercises: []
                                                    };
                                                  }
                                                  const updatedExercises = [...(updated[tgOriginalIndex].training_groups[trainingGroupIdx].exercises || [])];
                                                  updatedExercises[exerciseIdx] = {
                                                    ...updatedExercises[exerciseIdx],
                                                    sets: e.target.value ? parseInt(e.target.value) : null
                                                  };
                                                  updated[tgOriginalIndex].training_groups[trainingGroupIdx].exercises = updatedExercises;
                                                  setEditedRecords(updated);
                                                }}
                                                className="h-8 text-xs text-center w-full"
                                              />
                                            ) : (
                                              <span className="text-sm">{exercise.sets ? `${exercise.sets}x` : '–'}</span>
                                            )}
                                          </td>
                                          <td className="p-2 text-center">
                                            {isEditingThisGroup ? (
                                              <Input
                                                value={editedRecords[tgOriginalIndex]?.training_groups?.[trainingGroupIdx]?.exercises?.[exerciseIdx]?.reps || ''}
                                                onChange={(e) => {
                                                  const updated = [...editedRecords];
                                                  if (!updated[tgOriginalIndex].training_groups) {
                                                    updated[tgOriginalIndex].training_groups = [];
                                                  }
                                                  if (!updated[tgOriginalIndex].training_groups[trainingGroupIdx]) {
                                                    updated[tgOriginalIndex].training_groups[trainingGroupIdx] = {
                                                      group_name: focus,
                                                      exercises: []
                                                    };
                                                  }
                                                  const updatedExercises = [...(updated[tgOriginalIndex].training_groups[trainingGroupIdx].exercises || [])];
                                                  updatedExercises[exerciseIdx] = {
                                                    ...updatedExercises[exerciseIdx],
                                                    reps: e.target.value || null
                                                  };
                                                  updated[tgOriginalIndex].training_groups[trainingGroupIdx].exercises = updatedExercises;
                                                  setEditedRecords(updated);
                                                }}
                                                className="h-8 text-xs text-center w-full"
                                              />
                                            ) : (
                                              <span className="text-sm">{exercise.reps || '–'}</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Planos do tipo "daily" - todos os dias no mesmo card */}
              {dailyPlans.length > 0 && (() => {
                // Pegar o primeiro plano para usar como referência
                const firstPlan = dailyPlans[0];
                const firstOriginalIndex = firstPlan._index ?? 0;

                return (
                  <div key={`${dateKey}-daily-card`} className="border rounded-lg p-4 space-y-6">
                    {/* Header do card com nome e data do main_record editáveis */}
                    <div className="flex items-center justify-between border-b pb-3">
                      <div className="flex items-center gap-3 flex-1">
                        {editingMainRecord ? (
                          <div className="flex items-center gap-3 flex-1">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Nome:</Label>
                              <Input
                                value={editedMainRecord?.plan_name || ''}
                                onChange={(e) => {
                                  setEditedMainRecord({
                                    ...editedMainRecord!,
                                    plan_name: e.target.value
                                  });
                                }}
                                className="h-8 text-xs"
                                placeholder="Nome do plano"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Data:</Label>
                              <Input
                                type="date"
                                value={editedMainRecord?.event_date || editedMainRecord?.start_date || dateKey}
                                onChange={(e) => {
                                  setEditedMainRecord({
                                    ...editedMainRecord!,
                                    event_date: e.target.value,
                                    start_date: e.target.value
                                  });
                                  // Atualizar também a data em todos os planos
                                  const updated = [...editedRecords];
                                  dailyPlans.forEach((plan) => {
                                    const planIndex = plan._index ?? 0;
                                    if (updated[planIndex]) {
                                      updated[planIndex] = {
                                        ...updated[planIndex],
                                        event_date: e.target.value,
                                        start_date: e.target.value
                                      };
                                    }
                                  });
                                  setEditedRecords(updated);
                                }}
                                className="h-8 text-xs"
                              />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h3 className="text-lg font-bold">
                              {mainRecord?.plan_name || 'Plano de Treino Diário'} - {format(new Date(dateKey), "dd/MM/yyyy")}
                            </h3>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {editingMainRecord ? (
                          <>
                            <Button
                              size="sm"
                              onClick={handleSaveMainRecord}
                              disabled={isSaving}
                              className="h-8"
                            >
                              {isSaving ? "Salvando..." : "Salvar"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingMainRecord(false);
                                setEditedMainRecord(mainRecord);
                              }}
                              disabled={isSaving}
                              className="h-8"
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingMainRecord(true);
                            }}
                            className="h-8"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Todos os dias dentro do mesmo card */}
                    {dailyPlans.map((plan, planIndex) => {
                      const originalIndex = plan._index ?? planIndex;

                      return (
                        <div key={`${dateKey}-${originalIndex}`} className="space-y-4">
                          {/* Visualização em tabela para structure_type: "daily" */}
                          {plan.training_groups && plan.training_groups.length > 0 && plan.structure_type === 'daily' && (
                            <div className="space-y-6">
                              {plan.training_groups?.map((group, groupIdx) => {
                                const exercises = group.exercises || [];
                                const isEditingThisDay = editingIndex === originalIndex &&
                                  editingGroupIndex === groupIdx &&
                                  editingExerciseIndex === null;

                                // Formatar título: "{day_of_week} – {focus}"
                                const dayTitle = plan.day_of_week
                                  ? `${plan.day_of_week}${plan.focus ? ` – ${plan.focus}` : ''}`
                                  : plan.plan_name || 'Treino';

                                return (
                                  <div key={groupIdx} className="space-y-3">
                                    {/* Header do dia com botão de editar */}
                                    <div className="flex items-center justify-between border-b pb-2">
                                      <h3 className="text-base font-semibold">{dayTitle}</h3>
                                      <div className="flex gap-2">
                                        {isEditingThisDay ? (
                                          <>
                                            <Button
                                              size="sm"
                                              onClick={() => handleSave(originalIndex)}
                                              disabled={isSaving}
                                              className="h-7 text-xs"
                                            >
                                              {isSaving ? "Salvando..." : "Salvar"}
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => {
                                                setEditingIndex(null);
                                                setEditingGroupIndex(null);
                                                setEditingExerciseIndex(null);
                                                setEditedRecords(trainingPlans);
                                              }}
                                              disabled={isSaving}
                                              className="h-7 text-xs"
                                            >
                                              Cancelar
                                            </Button>
                                          </>
                                        ) : (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setEditingIndex(originalIndex);
                                              setEditingGroupIndex(groupIdx);
                                              setEditingExerciseIndex(null);
                                            }}
                                            className="h-7 text-xs"
                                          >
                                            <Pencil className="h-3 w-3 mr-1" />
                                            Editar
                                          </Button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Tabela de exercícios */}
                                    <div className="overflow-x-auto">
                                      <table className="w-full border-collapse text-sm table-fixed">
                                        <colgroup>
                                          <col className="w-[50%]" />
                                          <col className="w-[30%]" />
                                          <col className="w-[20%]" />
                                        </colgroup>
                                        <thead>
                                          <tr className="bg-muted/50">
                                            <th className="p-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-r">
                                              Exercício
                                            </th>
                                            <th className="p-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-r">
                                              Séries e Repetições
                                            </th>
                                            <th className="p-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b">
                                              Descanso
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {exercises.map((exercise, exerciseIdx) => (
                                            <tr key={exerciseIdx} className="border-b border-border hover:bg-muted/30 transition-colors">
                                              <td className="p-2 border-r">
                                                {isEditingThisDay ? (
                                                  <Input
                                                    value={editedRecords[originalIndex]?.training_groups?.[groupIdx]?.exercises?.[exerciseIdx]?.exercise || ''}
                                                    onChange={(e) => {
                                                      const updated = [...editedRecords];
                                                      if (!updated[originalIndex].training_groups) {
                                                        updated[originalIndex].training_groups = [];
                                                      }
                                                      if (!updated[originalIndex].training_groups[groupIdx]) {
                                                        updated[originalIndex].training_groups[groupIdx] = { group_name: group.group_name, exercises: [] };
                                                      }
                                                      const updatedExercises = [...(updated[originalIndex].training_groups[groupIdx].exercises || [])];
                                                      updatedExercises[exerciseIdx] = {
                                                        ...updatedExercises[exerciseIdx],
                                                        exercise: e.target.value
                                                      };
                                                      updated[originalIndex].training_groups[groupIdx].exercises = updatedExercises;
                                                      setEditedRecords(updated);
                                                    }}
                                                    className="h-8 text-xs w-full"
                                                    placeholder="Nome do exercício"
                                                  />
                                                ) : (
                                                  <span className="text-sm block truncate">{exercise.exercise}</span>
                                                )}
                                              </td>
                                              <td className="p-2 border-r text-center">
                                                {isEditingThisDay ? (
                                                  <div className="flex gap-1 items-center justify-center">
                                                    <Input
                                                      type="number"
                                                      value={editedRecords[originalIndex]?.training_groups?.[groupIdx]?.exercises?.[exerciseIdx]?.sets ?? ''}
                                                      onChange={(e) => {
                                                        const updated = [...editedRecords];
                                                        if (!updated[originalIndex].training_groups) {
                                                          updated[originalIndex].training_groups = [];
                                                        }
                                                        if (!updated[originalIndex].training_groups[groupIdx]) {
                                                          updated[originalIndex].training_groups[groupIdx] = { group_name: group.group_name, exercises: [] };
                                                        }
                                                        const updatedExercises = [...(updated[originalIndex].training_groups[groupIdx].exercises || [])];
                                                        updatedExercises[exerciseIdx] = {
                                                          ...updatedExercises[exerciseIdx],
                                                          sets: e.target.value ? parseInt(e.target.value) : null
                                                        };
                                                        updated[originalIndex].training_groups[groupIdx].exercises = updatedExercises;
                                                        setEditedRecords(updated);
                                                      }}
                                                      className="h-7 text-xs text-center w-12"
                                                      placeholder="Séries"
                                                    />
                                                    <span className="text-xs">x</span>
                                                    <Input
                                                      value={editedRecords[originalIndex]?.training_groups?.[groupIdx]?.exercises?.[exerciseIdx]?.reps || ''}
                                                      onChange={(e) => {
                                                        const updated = [...editedRecords];
                                                        if (!updated[originalIndex].training_groups) {
                                                          updated[originalIndex].training_groups = [];
                                                        }
                                                        if (!updated[originalIndex].training_groups[groupIdx]) {
                                                          updated[originalIndex].training_groups[groupIdx] = { group_name: group.group_name, exercises: [] };
                                                        }
                                                        const updatedExercises = [...(updated[originalIndex].training_groups[groupIdx].exercises || [])];
                                                        updatedExercises[exerciseIdx] = {
                                                          ...updatedExercises[exerciseIdx],
                                                          reps: e.target.value || null
                                                        };
                                                        updated[originalIndex].training_groups[groupIdx].exercises = updatedExercises;
                                                        setEditedRecords(updated);
                                                      }}
                                                      className="h-7 text-xs text-center flex-1"
                                                      placeholder="Repetições"
                                                    />
                                                  </div>
                                                ) : (
                                                  <span className="text-sm">
                                                    {exercise.sets ? `${exercise.sets}x` : ''} {exercise.reps || ''}
                                                  </span>
                                                )}
                                              </td>
                                              <td className="p-2 text-center">
                                                {isEditingThisDay ? (
                                                  <Input
                                                    value={editedRecords[originalIndex]?.training_groups?.[groupIdx]?.exercises?.[exerciseIdx]?.rest || ''}
                                                    onChange={(e) => {
                                                      const updated = [...editedRecords];
                                                      if (!updated[originalIndex].training_groups) {
                                                        updated[originalIndex].training_groups = [];
                                                      }
                                                      if (!updated[originalIndex].training_groups[groupIdx]) {
                                                        updated[originalIndex].training_groups[groupIdx] = { group_name: group.group_name, exercises: [] };
                                                      }
                                                      const updatedExercises = [...(updated[originalIndex].training_groups[groupIdx].exercises || [])];
                                                      updatedExercises[exerciseIdx] = {
                                                        ...updatedExercises[exerciseIdx],
                                                        rest: e.target.value || null
                                                      };
                                                      updated[originalIndex].training_groups[groupIdx].exercises = updatedExercises;
                                                      setEditedRecords(updated);
                                                    }}
                                                    className="h-8 text-xs text-center w-full"
                                                    placeholder="60s"
                                                  />
                                                ) : (
                                                  <span className="text-sm">{exercise.rest || '–'}</span>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {trainingPlans.length} plano(s) de treino em {sortedDates.length} data(s)
      </div>
    </div>
  );
}

